import React, { useState } from "react";
import {
  Table,
  Button,
  Space,
  Input,
  Select,
  Card,
  Typography,
  Tag,
  Modal,
  Form,
  Row,
  Col,
  message,
  Descriptions,
  Badge,
  Spin,
  Tooltip,
  Rate,
  DatePicker,
  Avatar,
  List,
  Statistic,
} from "antd";
import {
  EyeOutlined,
  ReloadOutlined,
  FileTextOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  TeamOutlined,
  HomeOutlined,
  StarOutlined,
  PlusOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  serviceRecordService,
  type ServiceRecord,
  type GetServiceRecordsParams,
  type CreateServiceRecordRequest,
} from "../services/serviceRecordService";
import moment from "moment";
import { customerService } from "../services/customerService";
import { courseService } from "../services/courseService";
import { roomService } from "../services/roomService";
import { employeeService } from "../services/employeeService";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const ServiceRecordsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useState<GetServiceRecordsParams>({
    page: 1,
    limit: 10,
  });
  const [selectedRecord, setSelectedRecord] = useState<ServiceRecord | null>(
    null
  );
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [completeModalVisible, setCompleteModalVisible] = useState(false);
  const [satisfactionModalVisible, setSatisfactionModalVisible] =
    useState(false);
  const [activeServicesModalVisible, setActiveServicesModalVisible] =
    useState(false);
  const [todayServicesModalVisible, setTodayServicesModalVisible] =
    useState(false);
  const [form] = Form.useForm();
  const [createForm] = Form.useForm();
  const [satisfactionForm] = Form.useForm();

  // Fetch customers for select
  const { data: customersData } = useQuery(["customers"], () =>
    customerService.getCustomers({ limit: 1000 })
  );

  // Fetch courses for select
  const { data: coursesData } = useQuery(["courses"], () =>
    courseService.getCourses({ limit: 1000 })
  );

  // Fetch courses for select
  const { data: roomsData } = useQuery(["rooms"], () =>
    roomService.getRooms({ limit: 1000 })
  );

  // Fetch employees
  const { data: employeesData } = useQuery(
    ["employees"],
    () => employeeService.getEmployees({ role: "therapist" }),
    { keepPreviousData: true }
  );

  // Query สำหรับดึงข้อมูลบันทึกการให้บริการ
  const {
    data: recordsResponse,
    isLoading,
    refetch,
  } = useQuery(
    ["serviceRecords", searchParams],
    () => serviceRecordService.getServiceRecords(searchParams),
    {
      keepPreviousData: true,
      onError: (error: any) => {
        message.error("ບໍ່ສາມາດໂຫລດຂໍ້ມູນບັນທຶກການໃຫ້ບໍລິການໄດ້");
        console.error("Error fetching service records:", error);
      },
    }
  );

  // Query สำหรับการให้บริการที่กำลังดำเนินการ
  const { data: activeServicesData, isLoading: isActiveLoading } = useQuery(
    ["activeServices"],
    () => serviceRecordService.getActiveServices(),
    {
      refetchInterval: 30000, // รีเฟรชทุก 30 วิນາທີ
      onError: (error: any) => {
        console.error("Error fetching active services:", error);
      },
    }
  );

  // Query สำหรับการให้บริการມື້ນີ້
  const { data: todayServicesData, isLoading: isTodayLoading } = useQuery(
    ["todayServices"],
    () => serviceRecordService.getTodayServices(),
    {
      onError: (error: any) => {
        console.error("Error fetching today services:", error);
      },
    }
  );

  // Mutation สำหรับสร้างบันทึกใหม่
  const createRecordMutation = useMutation(
    (data: CreateServiceRecordRequest) =>
      serviceRecordService.createServiceRecord(data),
    {
      onSuccess: () => {
        message.success("ສ້າງບັນທຶກການໃຫ້ບໍລິການສຳເລັດ");
        queryClient.invalidateQueries(["serviceRecords"]);
        queryClient.invalidateQueries(["activeServices"]);
        queryClient.invalidateQueries(["todayServices"]);
        setCreateModalVisible(false);
        createForm.resetFields();
      },
      onError: (error: any) => {
        message.error(
          error?.response?.data?.message ||
            "ບໍ່ສາມາດສ້າງບັນທຶກການໃຫ້ບໍລິການໄດ້"
        );
      },
    }
  );

  // Mutation สำหรับเสร็จสิ้นการให้บริการ
  const completeServiceMutation = useMutation(
    ({ id, data }: { id: string; data: any }) =>
      serviceRecordService.completeService(id, data),
    {
      onSuccess: () => {
        message.success("ສຳເລັດການໃຊ້ບໍລິການ");
        queryClient.invalidateQueries(["serviceRecords"]);
        queryClient.invalidateQueries(["activeServices"]);
        setCompleteModalVisible(false);
        setSelectedRecord(null);
        form.resetFields();
      },
      onError: (error: any) => {
        message.error(
          error?.response?.data?.message || "ບໍ່ສາມາດບັນທຶກການໃຫ້ບໍລິການໄດ້"
        );
      },
    }
  );

  // Mutation สำหรับແກ້ໄຂความพึงพอใจ
  const updateSatisfactionMutation = useMutation(
    ({
      id,
      rating,
      feedback,
    }: {
      id: string;
      rating: number;
      feedback?: string;
    }) => serviceRecordService.updateCustomerSatisfaction(id, rating, feedback),
    {
      onSuccess: () => {
        message.success("ບັນທຶກຄວາມພຶງພໍໃຈສຳເລັດ");
        queryClient.invalidateQueries(["serviceRecords"]);
        setSatisfactionModalVisible(false);
        setSelectedRecord(null);
        satisfactionForm.resetFields();
      },
      onError: (error: any) => {
        message.error(
          error?.response?.data?.message || "ບໍ່ສາມາດບັນທຶກຄວາມພຶງພໍໃຈໄດ້"
        );
      },
    }
  );

  const handleViewDetails = (record: ServiceRecord) => {
    setSelectedRecord(record);
    setDetailModalVisible(true);
  };

  const handleCompleteService = (record: ServiceRecord) => {
    setSelectedRecord(record);
    form.setFieldsValue({
      endTime: moment(),
    });
    setCompleteModalVisible(true);
  };

  const handleUpdateSatisfaction = (record: ServiceRecord) => {
    setSelectedRecord(record);
    satisfactionForm.setFieldsValue({
      rating: record.customerSatisfaction?.rating || 5,
      feedback: record.customerSatisfaction?.feedback || "",
    });
    setSatisfactionModalVisible(true);
  };

  const handleCreateSubmit = (values: any) => {
    const formData = {
      ...values,
      startTime: values.startTime.toISOString(),
      therapists: values.therapists.map((therapistId: string) => ({
        therapist: therapistId,
      })),
    };
    createRecordMutation.mutate(formData);
  };

  const handleCompleteSubmit = (values: any) => {
    if (!selectedRecord) return;
    const formData = {
      endTime: values.endTime.toISOString(),
    };
    completeServiceMutation.mutate({ id: selectedRecord._id, data: formData });
  };

  const handleSatisfactionSubmit = (values: any) => {
    if (!selectedRecord) return;
    updateSatisfactionMutation.mutate({
      id: selectedRecord._id,
      rating: values.rating,
      feedback: values.feedback,
    });
  };

  const handleSearch = (value: string) => {
    setSearchParams((prev) => ({ ...prev, search: value, page: 1 }));
  };

  const handleStatusFilter = (status?: string) => {
    setSearchParams((prev) => ({ ...prev, status: status as any, page: 1 }));
  };

  const handleDateFilter = (date: any) => {
    setSearchParams((prev) => ({
      ...prev,
      date: date ? date.format("YYYY-MM-DD") : undefined,
      page: 1,
    }));
  };

  const handleTableChange = (pagination: any) => {
    setSearchParams((prev) => ({
      ...prev,
      page: pagination.current,
      limit: pagination.pageSize,
    }));
  };

  const getStatusColor = (status: string) => {
    const colorMap = {
      in_progress: "processing",
      completed: "success",
      cancelled: "error",
    } as const;
    return colorMap[status as keyof typeof colorMap] || "default";
  };

  const getStatusText = (status: string) => {
    const statusMap = {
      in_progress: "ກຳລັງດຳເນີການ",
      completed: "ສຳເລັດ",
      cancelled: "ຍົກເລິກ",
    };
    return statusMap[status as keyof typeof statusMap] || status;
  };

  const columns = [
    {
      title: "ລະຫັດບັນທຶກ",
      dataIndex: "recordId",
      key: "recordId",
      width: 120,
      render: (recordId: string) => (
        <Text code style={{ fontSize: "12px" }}>
          {recordId}
        </Text>
      ),
    },
    {
      title: "ລູກຄ້າ",
      dataIndex: "customer",
      key: "customer",
      render: (customer: any) => (
        <div>
          <div
            style={{ fontWeight: 500, display: "flex", alignItems: "center" }}
          >
            <UserOutlined style={{ marginRight: 4, color: "#1890ff" }} />
            {customer?.name || "N/A"}
          </div>
          <div style={{ fontSize: "12px", color: "#666" }}>
            ID: {customer?.customerId || "N/A"}
          </div>
        </div>
      ),
    },
    {
      title: "ຄອສ",
      dataIndex: "course",
      key: "course",
      render: (course: any) => (
        <div>
          <div style={{ fontWeight: 500 }}>{course?.name || "N/A"}</div>
          <div style={{ fontSize: "12px", color: "#666" }}>
            {course?.duration || 0} ນາທີ
          </div>
        </div>
      ),
    },
    {
      title: "ຫ້ອງ",
      dataIndex: "room",
      key: "room",
      width: 100,
      render: (room: any) => (
        <div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <HomeOutlined style={{ marginRight: 4, color: "#52c41a" }} />
            <Text strong>{room?.roomNumber || "N/A"}</Text>
          </div>
          <div style={{ fontSize: "11px", color: "#666" }}>
            {room?.roomName || "N/A"}
          </div>
        </div>
      ),
    },
    {
      title: "ໝໍນວດ",
      dataIndex: "therapists",
      key: "therapists",
      width: 150,
      render: (therapists: any[]) => (
        <div>
          {therapists?.slice(0, 2).map((t, index) => (
            <div key={index} style={{ fontSize: "12px", marginBottom: 2 }}>
              <TeamOutlined style={{ marginRight: 4, color: "#722ed1" }} />
              {t.therapist?.name || "N/A"}
            </div>
          ))}
          {therapists?.length > 2 && (
            <Text style={{ fontSize: "11px", color: "#666" }}>
              +{therapists.length - 2} ຄົນ
            </Text>
          )}
        </div>
      ),
    },
    {
      title: "ເວລາ",
      key: "time",
      width: 120,
      render: (_: any, record: ServiceRecord) => (
        <div>
          <div style={{ fontSize: "12px" }}>
            ເລີ່ມ: {moment(record.startTime).format("HH:mm")}
          </div>
          {record.endTime && (
            <div style={{ fontSize: "12px", color: "#52c41a" }}>
              ສິ້ນສຸດ: {moment(record.endTime).format("HH:mm")}
            </div>
          )}
          {record.actualDuration && (
            <div style={{ fontSize: "11px", color: "#666" }}>
              {record.actualDuration} ນາທີ
            </div>
          )}
        </div>
      ),
    },
    {
      title: "ສະຖານະ",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status: string, record: ServiceRecord) => (
        <div>
          <Badge status={getStatusColor(status)} text={getStatusText(status)} />
          {record.customerSatisfaction?.rating && (
            <div style={{ marginTop: 4 }}>
              <Rate
                disabled
                value={record.customerSatisfaction.rating}
                style={{ fontSize: "12px" }}
              />
            </div>
          )}
        </div>
      ),
      filters: [
        { text: "ກຳລັງດຳເນີນການ", value: "in_progress" },
        { text: "ສຳເລັດ", value: "completed" },
        { text: "ຍົກເລິກ", value: "cancelled" },
      ],
    },
    {
      title: "ຈັດການ",
      key: "actions",
      width: 200,
      render: (_: any, record: ServiceRecord) => (
        <Space>
          <Tooltip title="ເບິ່ງລາຍລະອຽດ">
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          {record.status === "in_progress" && (
            <Tooltip title="ສຳເລັດການໃຫ້ບໍລິການ">
              <Button
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => handleCompleteService(record)}
                loading={completeServiceMutation.isLoading}
                style={{
                  backgroundColor: "#52c41a",
                  borderColor: "#52c41a",
                  color: "white",
                }}
              />
            </Tooltip>
          )}
          {record.status === "completed" &&
            !record.customerSatisfaction?.rating && (
              <Tooltip title="ປະເມີນຄວາມພຶງພໍໃຈ">
                <Button
                  size="small"
                  icon={<StarOutlined />}
                  onClick={() => handleUpdateSatisfaction(record)}
                  loading={updateSatisfactionMutation.isLoading}
                  style={{
                    backgroundColor: "#faad14",
                    borderColor: "#faad14",
                    color: "white",
                  }}
                />
              </Tooltip>
            )}
        </Space>
      ),
    },
  ];

  const records = recordsResponse?.data?.serviceRecords || [];
  const pagination = recordsResponse?.data?.pagination;
  const activeServices = activeServicesData?.data || [];
  const todayServices = todayServicesData?.data || [];

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          <FileTextOutlined style={{ marginRight: 8 }} />
          ບັນທຶກການໃຫ້ບໍລິການ
        </Title>
        <Space>
          <Button
            icon={<PlayCircleOutlined />}
            onClick={() => setActiveServicesModalVisible(true)}
            style={{
              backgroundColor: "#1890ff",
              borderColor: "#1890ff",
              color: "white",
            }}
          >
             ກຳລັງດຳເນີນການ ({activeServices.length})
          </Button>
          <Button
            icon={<CalendarOutlined />}
            onClick={() => setTodayServicesModalVisible(true)}
          >
            ບໍລິການມື້ນີ້ ({todayServices.length})
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            loading={isLoading}
          >
            ຮີເຟຣ
          </Button>
        </Space>
      </div>

      {/* Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="ບໍລິການທັງໝົດ"
              value={pagination?.total || 0}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="ກຳລັງດຳເນີນການ"
              value={activeServices.length}
              prefix={<PlayCircleOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="ສຳເລັດມື້ນີ້"
              value={
                todayServices.filter((s) => s.status === "completed").length
              }
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#3f8600" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="ຄວາມພຶງພໍໃຈສະເລ່ຍ"
              value={
                records.length > 0
                  ? (
                      records
                        .filter((r: any) => r.customerSatisfaction?.rating)
                        .reduce(
                          (acc: any, r: any) =>
                            acc + (r.customerSatisfaction?.rating || 0),
                          0
                        ) /
                        records.filter(
                          (r: any) => r.customerSatisfaction?.rating
                        ).length || 0
                    ).toFixed(1)
                  : "0"
              }
              prefix={<StarOutlined />}
              suffix="/ 5"
              valueStyle={{ color: "#faad14" }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8} md={6}>
            <Input.Search
              placeholder="ຄົ້ນຫາລູກຄ້າ, ຄອສ..."
              allowClear
              style={{ width: "100%" }}
              onSearch={handleSearch}
              enterButton
            />
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select
              placeholder="ເບິ່ງຕາມສະຖານະ"
              allowClear
              style={{ width: "100%" }}
              onChange={handleStatusFilter}
            >
              <Option value="in_progress">ກຳລັງດຳເນີນການ</Option>
              <Option value="completed">ສຳເລັດ</Option>
              <Option value="cancelled">ຍົກເລິກ</Option>
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <DatePicker
              placeholder="ເລືອກວັນທີ"
              style={{ width: "100%" }}
              onChange={handleDateFilter}
              allowClear
            />
          </Col>
          <Col xs={24} sm={24} md={6}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateModalVisible(true)}
              style={{ width: "100%" }}
              loading={createRecordMutation.isLoading}
            >
              ເລີ່ມການໃຫ້ບໍລິການ
            </Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={records}
          rowKey="_id"
          loading={isLoading}
          onChange={handleTableChange}
          pagination={{
            current: pagination?.current || 1,
            pageSize: searchParams.limit || 10,
            total: pagination?.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} ຈາກ ${total} ລາຍການ`,
          }}
        />
      </Card>

      {/* Service Record Detail Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <FileTextOutlined style={{ marginRight: 8 }} />
            {`ລາຍລະອຽດການໃຫ້ບໍລິການ: ${selectedRecord?.recordId}`}
          </div>
        }
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedRecord(null);
        }}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            ປິດ
          </Button>,
        ]}
        width={800}
      >
        {selectedRecord && (
          <div>
            <Descriptions bordered column={2} style={{ marginBottom: 24 }}>
              <Descriptions.Item label="ລະຫັດບັນທຶກ">
                <Text code>{selectedRecord.recordId}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="ສະຖານະ">
                <Badge
                  status={getStatusColor(selectedRecord.status)}
                  text={getStatusText(selectedRecord.status)}
                />
              </Descriptions.Item>
              <Descriptions.Item label="ລູກຄ້າ">
                <div>
                  <div style={{ fontWeight: 500 }}>
                    {selectedRecord.customer?.name || "N/A"}
                  </div>
                  <div style={{ fontSize: "12px", color: "#666" }}>
                    ID: {selectedRecord.customer?.customerId || "N/A"} | ໂທ:{" "}
                    {selectedRecord.customer?.phone || "N/A"}
                  </div>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="ຄອສ">
                <div>
                  <div style={{ fontWeight: 500 }}>
                    {selectedRecord.course?.name || "N/A"}
                  </div>
                  <div style={{ fontSize: "12px", color: "#666" }}>
                    ໄລຍະເວລາ: {selectedRecord.course?.duration || 0} ນາທີ
                  </div>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="ຫ້ອງ">
                <div>
                  <div style={{ fontWeight: 500 }}>
                    {selectedRecord.room?.roomNumber || "N/A"}
                  </div>
                  <div style={{ fontSize: "12px", color: "#666" }}>
                    {selectedRecord.room?.roomName || "N/A"}
                  </div>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="ເວລາເລີ່ມ">
                {moment(selectedRecord.startTime).format("DD/MM/YYYY HH:mm")}
              </Descriptions.Item>
              {selectedRecord.endTime && (
                <Descriptions.Item label="ເວລາສິ້ນສຸດ">
                  {moment(selectedRecord.endTime).format("DD/MM/YYYY HH:mm")}
                </Descriptions.Item>
              )}
              {selectedRecord.actualDuration && (
                <Descriptions.Item label="ໄລຍະເວລາໃຊ້ຈິງ">
                  {selectedRecord.actualDuration} ນາທີ
                </Descriptions.Item>
              )}
              {selectedRecord.booking && (
                <Descriptions.Item label="ການຈອງ" span={2}>
                  <Text code>{selectedRecord.booking.bookingId}</Text>
                </Descriptions.Item>
              )}
              {selectedRecord.notes && (
                <Descriptions.Item label="ໝາຍເຫດ" span={2}>
                  {selectedRecord.notes}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="ສ້າງໂດຍ" span={2}>
                <div>
                  <div>{selectedRecord.createdBy?.name || "N/A"}</div>
                  <div style={{ fontSize: "12px", color: "#666" }}>
                    ID: {selectedRecord.createdBy?.employeeId || "N/A"} |{" "}
                    {moment(selectedRecord.createdAt).format(
                      "DD/MM/YYYY HH:mm"
                    )}
                  </div>
                </div>
              </Descriptions.Item>
            </Descriptions>

            {/* Therapists Section */}
            <Card title="ໝໍນວດ" size="small" style={{ marginBottom: 16 }}>
              <List
                dataSource={selectedRecord.therapists}
                renderItem={(therapist) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        <Avatar
                          icon={<TeamOutlined />}
                          style={{ backgroundColor: "#722ed1" }}
                        />
                      }
                      title={
                        <div>
                          <span>{therapist.therapist?.name || "N/A"}</span>
                          {therapist.isOvertime && (
                            <Tag color="orange" style={{ marginLeft: 8 }}>
                              OT
                            </Tag>
                          )}
                        </div>
                      }
                      description={
                        <div>
                          <div>
                            ID: {therapist.therapist?.employeeId || "N/A"}
                          </div>
                          <div>
                            ເລີ່ມ: {moment(therapist.startTime).format("HH:mm")}
                          </div>
                          {therapist.endTime && (
                            <div>
                              ສິ້ນສຸດ:{" "}
                              {moment(therapist.endTime).format("HH:mm")}
                            </div>
                          )}
                          {therapist.hoursWorked > 0 && (
                            <div>
                              ຊົ່ວໂມງເຮັດວຽດ: {therapist.hoursWorked.toFixed(1)}{" "}
                              ຊົ່ວໂມງ.
                            </div>
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>

            {/* Customer Satisfaction */}
            {selectedRecord.customerSatisfaction && (
              <Card title="ຄວາມພຶງພໍໃຈຂອງລູກຄ້າ" size="small">
                <div style={{ textAlign: "center" }}>
                  <Rate
                    disabled
                    value={selectedRecord.customerSatisfaction.rating}
                    style={{ fontSize: "24px", marginBottom: 16 }}
                  />
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: 500,
                      marginBottom: 8,
                    }}
                  >
                    {selectedRecord.customerSatisfaction.rating}/5 ดาว
                  </div>
                  {selectedRecord.customerSatisfaction.feedback && (
                    <div
                      style={{
                        backgroundColor: "#f6ffed",
                        padding: 12,
                        borderRadius: 6,
                        border: "1px solid #b7eb8f",
                        textAlign: "left",
                      }}
                    >
                      <strong>ຄວາມຄິດເຫັນ:</strong>
                      <div style={{ marginTop: 4 }}>
                        {selectedRecord.customerSatisfaction.feedback}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            )}
          </div>
        )}
      </Modal>

      {/* Create Service Record Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <PlusOutlined style={{ marginRight: 8 }} />
            ເລີ່ມການໃຫ້ບໍລິການໃໝ່
          </div>
        }
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        footer={null}
        width={700}
      >
        <Spin spinning={createRecordMutation.isLoading}>
          <Form
            form={createForm}
            layout="vertical"
            onFinish={handleCreateSubmit}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="customer"
                  label="ລູກຄ້າ"
                  rules={[{ required: true, message: "ກະລຸນາເລືອກລູກຄ້າ" }]}
                >
                  <Select
                    placeholder="ເລືອກລູກຄ້າ"
                    showSearch
                    optionFilterProp="children"
                  >
                    {customersData?.data.customers?.map((customer: any) => (
                    <Option key={customer._id} value={customer._id}>
                      {customer.name} ({customer.phone})
                    </Option>
                  ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="course"
                  label="ຄອສ"
                  rules={[{ required: true, message: "ກະລຸນາເລືອກຄອສ" }]}
                >
                  <Select placeholder="ເລືອກຄອສ">
                   {coursesData?.data.courses?.map((course: any) => (
                    <Option key={course._id} value={course._id}>
                      {course.name} ({course.duration} ນາທີ)
                    </Option>
                  ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="room"
                  label="ຫ້ອງ"
                  rules={[{ required: true, message: "ກະລຸນາເລືອກຫ້ອງ" }]}
                >
                  <Select placeholder="ເລືອກຫ້ອງ">
                   {roomsData?.data.rooms?.map((room: any) => (
                    <Option key={room._id} value={room._id}>
                      {room.roomName} ({room.roomNumber})
                    </Option>
                  ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="startTime"
                  label="ເວລາເລີ່ມ"
                  rules={[{ required: true, message: "ກະລຸນາເລືອກເວລາເລີ່ມ" }]}
                  initialValue={moment()}
                >
                  <DatePicker
                    showTime={{ format: "HH:mm" }}
                    format="DD/MM/YYYY HH:mm"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="therapists"
              label="ໝໍນວດ"
              rules={[
                { required: true, message: "ກະລຸນາເລືອກໝໍນວດຢ່າງນ້ອຍ 1 ຄົນ" },
                {
                  type: "array",
                  min: 1,
                  message: "ກະລຸນາເລືອກໝໍນວດຢ່າງນ້ອຍ 1 ຄົນ",
                },
              ]}
            >
              <Select mode="multiple" placeholder="ເລືອກໝໍນວດ">
                  {employeesData?.data.employees?.map((emp: any) => (
                    <Option key={emp._id} value={emp._id}>
                      {emp.name} ({emp.employeeId})
                    </Option>
                  ))}
              </Select>
            </Form.Item>

            <Form.Item name="notes" label="ໝາຍເຫດ">
              <TextArea rows={3} placeholder="ໝາຍເຫດເພີ່ມ (ບໍ່ບັງຄັບ)" />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={createRecordMutation.isLoading}
                >
                  ເລີ່ມການໃຫ້ບໍລິການ
                </Button>
                <Button
                  onClick={() => {
                    setCreateModalVisible(false);
                    createForm.resetFields();
                  }}
                  disabled={createRecordMutation.isLoading}
                >
                  ຍົກເລິກ
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      {/* Complete Service Modal */}
      <Modal
        title="ສຳເລັດການໃຫ້ບໍລິການ"
        open={completeModalVisible}
        onCancel={() => {
          setCompleteModalVisible(false);
          setSelectedRecord(null);
          form.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Spin spinning={completeServiceMutation.isLoading}>
          {selectedRecord && (
            <div
              style={{
                marginBottom: 16,
                padding: 12,
                backgroundColor: "#f6ffed",
                borderRadius: 6,
                border: "1px solid #b7eb8f",
              }}
            >
              <div>
                ລູກຄ້າ: <Text strong>{selectedRecord.customer?.name}</Text>
              </div>
              <div>
                ຄອສ: <Text strong>{selectedRecord.course?.name}</Text>
              </div>
              <div>
                ຫ້ອງ: <Text strong>{selectedRecord.room?.roomNumber}</Text>
              </div>
              <div>
                ເລີ່ມ:{" "}
                <Text>{moment(selectedRecord.startTime).format("HH:mm")}</Text>
              </div>
            </div>
          )}

          <Form form={form} layout="vertical" onFinish={handleCompleteSubmit}>
            <Form.Item
              name="endTime"
              label="ເວລາສິ້ນສຸດ"
              rules={[{ required: true, message: "ກະລຸນາປ້ອນເວລາສິ້ນສຸດ" }]}
            >
              <DatePicker
                showTime={{ format: "HH:mm" }}
                format="DD/MM/YYYY HH:mm"
                style={{ width: "100%" }}
              />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={completeServiceMutation.isLoading}
                >
                  ສຳເລັດການໃຫ້ບໍລິການ
                </Button>
                <Button
                  onClick={() => {
                    setCompleteModalVisible(false);
                    setSelectedRecord(null);
                    form.resetFields();
                  }}
                  disabled={completeServiceMutation.isLoading}
                >
                  ຍົກເລິກ
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      {/* Customer Satisfaction Modal */}
      <Modal
        title="ປະເມີນຄວາມພຶງພໍໃຈຂອງລູກຄ້າ"
        open={satisfactionModalVisible}
        onCancel={() => {
          setSatisfactionModalVisible(false);
          setSelectedRecord(null);
          satisfactionForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Spin spinning={updateSatisfactionMutation.isLoading}>
          {selectedRecord && (
            <div
              style={{
                marginBottom: 16,
                padding: 12,
                backgroundColor: "#f6ffed",
                borderRadius: 6,
                border: "1px solid #b7eb8f",
              }}
            >
              <div>
                ລູກຄ້າ: <Text strong>{selectedRecord.customer?.name}</Text>
              </div>
              <div>
                ຄອສ: <Text strong>{selectedRecord.course?.name}</Text>
              </div>
            </div>
          )}

          <Form
            form={satisfactionForm}
            layout="vertical"
            onFinish={handleSatisfactionSubmit}
          >
            <Form.Item
              name="rating"
              label="ຄະແນນຄວາມພຶງພໍໃຈ"
              rules={[{ required: true, message: "ກະລຸນາໃຫ້ຄະແນນ" }]}
            >
              <Rate style={{ fontSize: "32px" }} />
            </Form.Item>

            <Form.Item name="feedback" label="ຄວາມເຫັນເພີ່ມເຕີມ">
              <TextArea
                rows={4}
                placeholder="ຄວາມເຫັນຈາກລູກຄ້າ (ບໍ່ບັງຄັບ)"
                showCount
                maxLength={500}
              />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={updateSatisfactionMutation.isLoading}
                >
                  ບັນທຶກຄວາມພຶງພໍໃຈ
                </Button>
                <Button
                  onClick={() => {
                    setSatisfactionModalVisible(false);
                    setSelectedRecord(null);
                    satisfactionForm.resetFields();
                  }}
                  disabled={updateSatisfactionMutation.isLoading}
                >
                  ຍົກເລິກ
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      {/* Active Services Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <PlayCircleOutlined style={{ marginRight: 8, color: "#1890ff" }} />
            ບໍລິການທີ່ກຳລັງດຳເນີນການ
          </div>
        }
        open={activeServicesModalVisible}
        onCancel={() => setActiveServicesModalVisible(false)}
        footer={[
          <Button
            key="close"
            onClick={() => setActiveServicesModalVisible(false)}
          >
            ປິດ
          </Button>,
        ]}
        width={800}
      >
        <Spin spinning={isActiveLoading}>
          {activeServices.length > 0 ? (
            <List
              dataSource={activeServices}
              renderItem={(service) => (
                <List.Item
                  actions={[
                    <Button
                      size="small"
                      type="primary"
                      onClick={() => {
                        setActiveServicesModalVisible(false);
                        handleCompleteService(service);
                      }}
                    >
                      ສຳເລັດ
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        icon={<UserOutlined />}
                        style={{ backgroundColor: "#1890ff" }}
                      />
                    }
                    title={
                      <div>
                        <Text strong>{service.customer?.name}</Text> -{" "}
                        {service.course?.name}
                        <Tag color="blue" style={{ marginLeft: 8 }}>
                          {service.room?.roomNumber}
                        </Tag>
                      </div>
                    }
                    description={
                      <div>
                        <div>
                          ເລີ່ມ: {moment(service.startTime).format("HH:mm")} |
                          ໄລຍະເວລາ:{" "}
                          {Math.round(
                            moment().diff(moment(service.startTime), "minutes")
                          )}{" "}
                          ນາທີ
                        </div>
                        <div>
                          ໝໍນວດ:{" "}
                          {service.therapists
                            .map((t) => t.therapist?.name)
                            .join(", ")}
                        </div>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          ) : (
            <div
              style={{ textAlign: "center", padding: "40px 0", color: "#666" }}
            >
              <PlayCircleOutlined style={{ fontSize: 48, marginBottom: 16 }} />
              <div>ບໍ່ມີລາຍການໃຫ້ບໍລິການເທື່ອ</div>
            </div>
          )}
        </Spin>
      </Modal>

      {/* Today Services Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <CalendarOutlined style={{ marginRight: 8 }} />
            ລູກຄ້າໃຊ້ບໍລິການມື້ນີ້ ({moment().format("DD/MM/YYYY")})
          </div>
        }
        open={todayServicesModalVisible}
        onCancel={() => setTodayServicesModalVisible(false)}
        footer={[
          <Button
            key="close"
            onClick={() => setTodayServicesModalVisible(false)}
          >
            ປິດ
          </Button>,
        ]}
        width={800}
      >
        <Spin spinning={isTodayLoading}>
          {todayServices.length > 0 ? (
            <List
              dataSource={todayServices}
              renderItem={(service) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        icon={
                          service.status === "completed" ? (
                            <CheckCircleOutlined />
                          ) : service.status === "in_progress" ? (
                            <PlayCircleOutlined />
                          ) : (
                            <ClockCircleOutlined />
                          )
                        }
                        style={{
                          backgroundColor:
                            service.status === "completed"
                              ? "#52c41a"
                              : service.status === "in_progress"
                              ? "#1890ff"
                              : "#faad14",
                        }}
                      />
                    }
                    title={
                      <div>
                        <Text strong>{service.customer?.name}</Text> -{" "}
                        {service.course?.name}
                        <Tag
                          color={getStatusColor(service.status)}
                          style={{ marginLeft: 8 }}
                        >
                          {getStatusText(service.status)}
                        </Tag>
                      </div>
                    }
                    description={
                      <div>
                        <div>
                          ຫ້ອງ: {service.room?.roomNumber} | ເວລາ:{" "}
                          {moment(service.startTime).format("HH:mm")}
                          {service.endTime &&
                            ` - ${moment(service.endTime).format("HH:mm")}`}
                        </div>
                        <div>
                          ໝໍນວດ:{" "}
                          {service.therapists
                            .map((t) => t.therapist?.name)
                            .join(", ")}
                        </div>
                        {service.customerSatisfaction?.rating && (
                          <div style={{ marginTop: 4 }}>
                            <Rate
                              disabled
                              value={service.customerSatisfaction.rating}
                              style={{ fontSize: "12px" }}
                            />
                          </div>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          ) : (
            <div
              style={{ textAlign: "center", padding: "40px 0", color: "#666" }}
            >
              <CalendarOutlined style={{ fontSize: 48, marginBottom: 16 }} />
              <div>ຍັງບໍ່ມີລູກຄ້າໃຊ້ບໍລິການມື້ນີ້</div>
            </div>
          )}
        </Spin>
      </Modal>
    </div>
  );
};

export default ServiceRecordsPage;
